import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
    useCallback,
    useRef,
} from 'react'
import axios from 'axios'
import { GiftRecipient, PaidSubscription, User } from '../types/users'
import { BASE_URL, authToken } from '../../../../../.././config/constants'

const USER_IDENTITY_CACHE_KEY = 'reception:user-identities:v1'

interface UsersContextProps {
    users: User[] | null
    giftRecipients: GiftRecipient[] | null
    revalidateUser: () => Promise<void>
    revalidateUsers: () => Promise<void>
    updateUser: (id: number, updatedUser: Partial<User>) => Promise<void>
    updateUserPaidSubscription: (
        userId: number,
        updatedSubscription: PaidSubscription
    ) => void
    fetchUserDetails: (userId: number) => Promise<User | null>
    isMutatingUser: boolean
    isLoadingUser: boolean
    errorUser: string | null
}

const UserContext = createContext<UsersContextProps | undefined>(undefined)

const canUseLocalStorage = () =>
    typeof window !== 'undefined' && window.localStorage

const readCachedUsers = (): User[] | null => {
    if (!canUseLocalStorage()) {
        return null
    }

    try {
        const cachedUsers = window.localStorage.getItem(USER_IDENTITY_CACHE_KEY)
        return cachedUsers ? JSON.parse(cachedUsers) : null
    } catch (error) {
        console.warn('Failed to read cached reception user identities:', error)
        return null
    }
}

const writeCachedUsers = (users: User[]) => {
    if (!canUseLocalStorage()) {
        return
    }

    const identities = users.map((user) => ({
        id: user.id,
        username: user.username,
        email: user.email,
        name: user.name,
        surname: user.surname,
        paid_subscriptions: [],
    }))

    try {
        window.localStorage.setItem(
            USER_IDENTITY_CACHE_KEY,
            JSON.stringify(identities)
        )
    } catch (error) {
        console.warn('Failed to cache reception user identities:', error)
    }
}

export const UsersProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const [users, setUsers] = useState<User[] | null>(() => readCachedUsers())
    const [giftRecipients, setGiftRecipients] = useState<
        GiftRecipient[] | null
    >(null)
    const [loadingUserRequests, setLoadingUserRequests] = useState(0)
    const [isMutatingUser, setIsMutatingUser] = useState(false)
    const [errorUser, setErrorUser] = useState<string | null>(null)
    const usersRequestRef = useRef<Promise<void> | null>(null)
    const giftRecipientsRequestRef = useRef<Promise<void> | null>(null)
    const detailedUserIdsRef = useRef<Set<number>>(new Set())
    const userDetailsRequestRef = useRef<Map<number, Promise<User | null>>>(
        new Map()
    )
    const isLoadingUser = loadingUserRequests > 0

    const startLoadingUser = () =>
        setLoadingUserRequests((current) => current + 1)

    const finishLoadingUser = () =>
        setLoadingUserRequests((current) => Math.max(0, current - 1))

    const fetchUsers = async () => {
        if (usersRequestRef.current) {
            return usersRequestRef.current
        }

        startLoadingUser()
        setErrorUser(null)

        const request = (async () => {
            const userResponse = await axios.get(
                `${BASE_URL}/api/users?populate[0]=paid_subscriptions&populate[1]=role`,
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                }
            )

            detailedUserIdsRef.current.clear()
            setUsers(userResponse.data)
            writeCachedUsers(userResponse.data)
        })()

        usersRequestRef.current = request

        try {
            await request
        } catch (error: any) {
            console.error('Error fetching Users:', error)
            setErrorUser('Failed to fetch Users.')
        } finally {
            usersRequestRef.current = null
            finishLoadingUser()
        }
    }

    const fetchGiftRecipients = async () => {
        if (giftRecipientsRequestRef.current) {
            return giftRecipientsRequestRef.current
        }

        startLoadingUser()
        setErrorUser(null)

        const request = (async () => {
            const response = await axios.get(
                `${BASE_URL}/api/list-of-people-with-a-gifts?populate[courses_schedules]=*`,
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                }
            )
            setGiftRecipients(response.data.data)
        })()

        giftRecipientsRequestRef.current = request

        try {
            await request
        } catch (error: any) {
            console.error('Error fetching Gift Recipients:', error)
            setErrorUser('Failed to fetch Gift Recipients.')
        } finally {
            giftRecipientsRequestRef.current = null
            finishLoadingUser()
        }
    }

    const fetchUserDetails = async (userId: number): Promise<User | null> => {
        const cachedUser = users?.find((user) => user.id === userId)
        if (
            cachedUser &&
            detailedUserIdsRef.current.has(userId) &&
            (cachedUser.classes || cachedUser.reservations)
        ) {
            return cachedUser
        }

        const inFlightRequest = userDetailsRequestRef.current.get(userId)
        if (inFlightRequest) {
            return inFlightRequest
        }

        const request = (async () => {
            try {
                const response = await axios.get(
                    `${BASE_URL}/api/users/${userId}?populate[0]=paid_subscriptions&populate[1]=role&populate[2]=classes&populate[3]=reservations`,
                    {
                        headers: { Authorization: `Bearer ${authToken}` },
                    }
                )
                const detailedUser = response.data

                detailedUserIdsRef.current.add(userId)
                setUsers((prev) => {
                    const currentUsers = prev ?? []
                    const hasUser = currentUsers.some(
                        (user) => user.id === userId
                    )
                    const nextUsers = hasUser
                        ? currentUsers.map((user) =>
                              user.id === userId
                                  ? { ...user, ...detailedUser }
                                  : user
                          )
                        : [...currentUsers, detailedUser]

                    writeCachedUsers(nextUsers)
                    return nextUsers
                })

                return detailedUser
            } catch (error: any) {
                console.error('Error fetching user details:', error)
                return null
            } finally {
                userDetailsRequestRef.current.delete(userId)
            }
        })()

        userDetailsRequestRef.current.set(userId, request)
        return request
    }

    const updateUser = async (id: number, updatedUser: Partial<User>) => {
        setIsMutatingUser(true)
        setErrorUser(null)
        try {
            await axios.put(
                `${BASE_URL}/api/users/${id}`,
                { data: updatedUser },
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                }
            )
        } catch (error: any) {
            console.error('Error updating User:', error)
            setErrorUser(`Failed to update User ${id}.`)
        } finally {
            setIsMutatingUser(false)
        }
    }

    const revalidateUser = async () => {
        await fetchUsers()
    }

    const revalidateUsers = async () => {
        await Promise.all([fetchUsers(), fetchGiftRecipients()])
    }

    const updateUserPaidSubscription = useCallback(
        (userId: number, updatedSubscription: PaidSubscription) => {
            setUsers(
                (prev) =>
                    prev?.map((user) =>
                        user.id === userId
                            ? {
                                  ...user,
                                  paid_subscriptions:
                                      user.paid_subscriptions.map((sub) =>
                                          sub.id === updatedSubscription.id
                                              ? updatedSubscription
                                              : sub
                                      ),
                              }
                            : user
                    ) ?? null
            )
        },
        []
    )

    useEffect(() => {
        fetchUsers()
        fetchGiftRecipients()
    }, [])

    return (
        <UserContext.Provider
            value={{
                users,
                giftRecipients,
                revalidateUser,
                revalidateUsers,
                updateUser,
                updateUserPaidSubscription,
                fetchUserDetails,
                isMutatingUser,
                isLoadingUser,
                errorUser,
            }}
        >
            {children}
        </UserContext.Provider>
    )
}

export const useUsers = () => {
    const context = useContext(UserContext)
    if (!context) {
        throw new Error('useUser must be used within UserProvider')
    }
    return context
}
